import {
  Box,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import AddButton from "../../../../common/components/functional/addButton/AddButton";
import DialogComponent from "../../../../common/components/functional/dialogs/DialogComponent";
import { ReactComponent as RemoveFilled } from "../../../../common/icons/delete.svg";
import { ReactComponent as EditFilled } from "../../../../common/icons/editFilled.svg";
import { Nullable } from "../../../../common/types/nullable";
import { canExpertEditWithholdingTaxCorrection } from "../../../user/accessRightChecks";
import { AccessRightContext } from "../../../user/AccessRightProvider";
import AddEditModal from "./AddEditModal";
import AddModal from "./AddModal";
import {
  createTalentTaxWithholdingCorrection,
  deleteTalentTaxWithholdingCorrection,
  fetchTalentTaxWithholdingCorrections,
  selectAllTaxWithholdingCorrections,
  updateTalentTaxWithholdingCorrection,
} from "./taxWithholdingCorrectionSlice";
import {
  CreateTaxWithholdingCorrection,
  DeleteTaxWithholdingCorrection,
  TalentTaxWithholdingCorrection,
  UpdateTaxWithholdingCorrection,
} from "./types";

export interface CreateCorrection {
  talentId: string;
  amount?: number;
  date?: string;
  comment?: string;
}

interface Props {
  talentId: string;
  isReadOnly: boolean;
}

const WitholdingTaxCorrection = ({ talentId, isReadOnly }: Props) => {
  const [editedItem, setEditedItem] =
    useState<Nullable<TalentTaxWithholdingCorrection>>(null);
  //TODO: fetching or changing correction data when payslip is updated

  const [newItem, setNewItem] = useState<Nullable<CreateCorrection>>(null);

  const { t } = useTranslation();
  const { canEditWithholdingTaxCorrection } = useContext(AccessRightContext);
  const [corrections, setCorrections] = useState<
    TalentTaxWithholdingCorrection[]
  >([]);

  const dispatch = useAppDispatch();

  const storedCorrections: TalentTaxWithholdingCorrection[] = useAppSelector(
    selectAllTaxWithholdingCorrections,
  );

  useEffect(() => {
    dispatch(fetchTalentTaxWithholdingCorrections(talentId));
  }, [dispatch, talentId]);

  useEffect(() => {
    setCorrections([...storedCorrections]);
  }, [storedCorrections]);

  const onAddSubmit = (command: CreateTaxWithholdingCorrection) => {
    dispatch(createTalentTaxWithholdingCorrection(command));
  };

  const onEditSubmit = (command: UpdateTaxWithholdingCorrection) => {
    dispatch(updateTalentTaxWithholdingCorrection(command));
  };

  const onDeleteSubmit = (command: DeleteTaxWithholdingCorrection) => {
    dispatch(deleteTalentTaxWithholdingCorrection(command));
  };

  const closeEditingModal = () => {
    setEditedItem(null);
  };

  const closeAddModal = () => {
    setNewItem(null);
  };

  const cellStyle = { paddingLeft: "0px", fontSize: "12px" };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        background: "#FFFFFF",
        borderRadius: "8px",
        marginTop: "16px",
        padding: "0px 16px 0px 0px",
        height: "calc(100% - 16px)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <DialogTitle>
          <Typography
            variant="header"
            sx={{ fontWeight: 600, fontSize: "14px" }}
          >
            {t("talentProfile:witholdingTaxCorrection").toUpperCase()}
          </Typography>
        </DialogTitle>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          padding: "0px 0px 16px 16px",
          fontSize: "12px",
        }}
      >
        <TableContainer>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell sx={cellStyle}>
                  {t("talentProfile:correction")}
                </TableCell>
                <TableCell sx={cellStyle}>
                  {t("talentProfile:comment")}
                </TableCell>
                <TableCell sx={cellStyle}>{t("talentProfile:date")}</TableCell>
                <TableCell sx={cellStyle}>
                  {t("talentProfile:payslipId")}
                </TableCell>
                <TableCell />
                <TableCell />
              </TableRow>
              {corrections?.map(
                (correction: TalentTaxWithholdingCorrection, index: number) => (
                  <TableRow key={index}>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        paddingLeft: "0px",
                        width: "80px",
                      }}
                    >
                      {`${correction?.amount}€`}
                    </TableCell>
                    <TableCell sx={{ paddingLeft: "0px" }}>
                      {correction?.comment}
                    </TableCell>
                    <TableCell sx={{ paddingLeft: "0px" }}>
                      {format(new Date(correction?.date), "dd.MM.yyyy")}
                    </TableCell>
                    <TableCell sx={{ paddingLeft: "0px", width: "120px" }}>
                      {correction?.payslipNumber
                        ? `#${correction.payslipNumber}`
                        : ""}
                    </TableCell>
                    <TableCell sx={{ paddingLeft: "0px", width: "30px" }}>
                      {canEditWithholdingTaxCorrection && (
                        <EditFilled
                          onClick={() => setEditedItem({ ...correction })}
                          style={{
                            width: "16px",
                            height: "16px",
                            cursor: "pointer",
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ paddingLeft: "0px", width: "30px" }}>
                      {!correction.isLocked &&
                        canEditWithholdingTaxCorrection && (
                          <RemoveFilled
                            onClick={() =>
                              onDeleteSubmit({
                                id: correction.id,
                                version: correction.version,
                              })
                            }
                            style={{
                              width: "16px",
                              height: "16px",
                              cursor: "pointer",
                            }}
                          />
                        )}
                    </TableCell>
                  </TableRow>
                ),
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box
          sx={{
            marginTop: "16px",
            alignSelf: "flex-start",
          }}
        >
          <AddButton
            variant="outlined"
            label={t("talentProfile:addWitholdingTaxCorrection")}
            onClick={() =>
              setNewItem({
                talentId: talentId,
              })
            }
            disabled={isReadOnly}
            accessRightChecks={[canExpertEditWithholdingTaxCorrection]}
          />
        </Box>
      </Box>
      {editedItem && (
        <DialogComponent
          open={!!editedItem}
          onClose={closeEditingModal}
          autoWidth
          DialogContentProps={{
            sx: { padding: "0px" },
          }}
        >
          <AddEditModal
            onEditSubmit={onEditSubmit}
            onEditCancel={closeEditingModal}
            item={editedItem}
          />
        </DialogComponent>
      )}
      {newItem && (
        <DialogComponent
          open={!!newItem}
          onClose={closeAddModal}
          autoWidth
          DialogContentProps={{ sx: { padding: "0px" } }}
        >
          <AddModal
            onSubmit={onAddSubmit}
            onCancel={closeAddModal}
            item={newItem}
          />
        </DialogComponent>
      )}
    </Box>
  );
};

export default WitholdingTaxCorrection;
